/**
 * Shared translations — German (default), English, French.
 *
 * Originally written for the live2 board; promoted here so the analysis views
 * use the same dictionary and the same language choice rather than a second
 * copy. Hand-rolled rather than i18next: the string count is low, the app is
 * internal, and a dependency would cost more in build weight than it saves.
 *
 * KEY NAMESPACES
 *   sidebar. / view. / occ. / terminus. / time. / status. / reason.   live2 board
 *   an. / metrics. / acc. / filter.                                   analysis views
 * The live2 keys keep their original names so no live2 file needs editing.
 *
 * NOTE ON TIME: language changes formatting only. Times stay Europe/Berlin in
 * every language, because the board reports Stuttgart operations whoever reads it.
 */

export type Lang = 'de' | 'en' | 'fr';

export const LANGS: Lang[] = ['de', 'en', 'fr'];

export const LOCALE: Record<Lang, string> = {
  de: 'de-DE',
  en: 'en-GB',
  fr: 'fr-FR',
};

type Dict = Record<string, string>;

const de: Dict = {
  // ---- live2 board ----
  'sidebar.title': 'LIVE ZÜGE',
  'sidebar.count': '{n} aktiv',
  'sidebar.none': 'Keine aktiven Züge',
  'sidebar.legend': 'Zugstatus',
  'sidebar.recent': 'Gerade aktualisiert',

  'status.ACTIVE': 'Unterwegs',
  'status.ARRIVED': 'Angekommen',
  'status.SCHEDULED': 'Geplant',
  'status.NO_DATA': 'Keine Daten',
  'status.CANCELLED': 'Ausgefallen',

  'view.noSelection': 'Keine aktiven Züge verfügbar.',
  'view.loading': 'Daten werden geladen…',
  'view.noData': 'Keine Daten für diesen Zug.',
  'view.localTime': '(Ortszeit Deutschland)',
  'view.pastStops': 'Vorherige Halte ({n})',
  'view.hide': 'ausblenden',
  'view.show': 'anzeigen',
  'view.currentStop': 'AKTUELLER HALT',
  'view.nextStops': 'Folgende Halte',
  'view.route': 'Fahrtverlauf',
  'view.stopCount': '{n} Halte',
  'view.counts': 'Zählungen {measured}/{called}',
  'view.awaiting': '{n} laufend',
  'view.missing': '{n} fehlend',

  'occ.forecast': 'Prognose',
  'occ.awaiting': 'Zählung läuft',
  'occ.missing': 'keine Zählung',
  'occ.none': 'keine Daten',
  'occ.variance': 'n1 Prognoseabweichung',
  'occ.delta': 'Abweichung zur Prognose (ein Halt vorher)',
  'terminus.arrived': 'Angekommen',
  'terminus.expected': 'Erwartete Ankunft',

  'time.arr': 'An.',
  'time.dep': 'Ab.',

  'reason.NO_UICS': 'keine Wagen-Daten',
  'reason.INVALID_CARRIAGES': 'ungültige Wagenzahl',
  'reason.UIC_EXCEPTION': 'Fehler bei Wagen-Abfrage',
  'reason.BUILD_FAILED': 'Meldung nicht erstellt',
  'reason.BAD_RESULT': 'unerwartetes Ergebnis',
  'reason.PUBLISH_FAILED': 'Senden fehlgeschlagen',
  'reason.SKIPPED_PUBLISH': 'Senden übersprungen',
  'reason.NO_VU_MESSAGE': 'keine VU-Meldung',

  // ---- analysis page ----
  'an.analysis': 'Analyse',
  'an.missingMessages': 'Fehlende Meldungen',
  'an.hideSidebar': 'Ausblenden',
  'an.showTrains': 'Züge',
  'an.selectTrain': 'Zug auswählen',
  'an.selectTrainHint': 'Wählen Sie einen Zug aus der Seitenleiste, um Haltdetails zu sehen',
  'an.date': 'Datum',
  'an.filter': 'Filter',
  'an.pickDate': 'Datum wählen',
  'an.line': 'Linie',
  'an.trains': 'Züge',
  'an.noTrains': 'Keine Züge gefunden.',
  'an.staleShow': 'STALE',
  'an.staleHide': 'STALE AUS',

  'filter.live': 'Live',
  'filter.recent': 'Aktuell',
  'filter.all': 'Alle',

  'metrics.title': 'Linie {line} — Nachrichten',
  'metrics.total': 'Gesamt',
  'metrics.published': 'Gesendet',
  'metrics.failed': 'Fehler',
  'metrics.noStatus': 'Kein St.',
  'metrics.successRate': '{pct}% Erfolgsquote',

  'acc.title': 'Prognosegüte',
  'acc.lineTitle': 'Linie {line} — Prognosegüte',
  'acc.noData': 'Keine Daten',
  'acc.all': 'Alle',
  'acc.modelClass': 'Modellklasse',
  'acc.asSent': 'als gesendet',
  'acc.samples': 'n={n} Stichproben',
  'acc.hnError': 'H→N Fehler',
  'acc.ofHigh': 'von {n} HOCH',
  'acc.mae': 'MAE',
  'acc.persons': 'Pers.',
  'acc.details': 'Details',
  'acc.lineLabel': 'Linie {line}',
  'acc.allLines': 'Alle Linien',
  'acc.partialDay': 'Teiltag',
  'acc.noSample': 'keine Stichprobe',
  'acc.placeholder': 'BEISPIELDATEN',

  // ---- landing page ----
  'home.nav.liveBoard': 'Live-Board',
  'home.nav.liveMap': 'Live-Karte',
  'home.nav.analytics': 'Auswertung',
  'home.total': 'Gesamt',
  'home.activeJourneys': 'Aktive Zugfahrten',
  'home.liveUnits': 'Live (Fahrzeuge)',
  'home.unitsN': '{n} Fahrzeuge',
  'home.unitsEstN': '{n} Fahrzeuge (gesch.)',
  'home.missingFormation': 'Ohne Fahrzeugdaten',
  'home.noReading': 'Ohne Messung',
  'home.countedShare': '{pct}% direkt gezählt',
  'home.journeysEstimated': '{n} Fahrten geschätzt',
  'home.unitsComposition': 'Fahrzeuge: AFZ + WLAN + geschätzt',
  'home.pax': 'Fahrgäste an Bord',
  'home.trips': 'Fahrgastfahrten (geschätzt)',
  'home.tripsHint': '{pct}% der Fahrzeuge mit AFZ gezählt',
  'home.lastNDays': '{n} Tage',
  'home.daysWithData': 'nur {have} von {want} Tagen mit Daten',
  'home.live': 'Live',
  'home.day': 'Tag',
  'home.week': 'Woche',
  'home.month': 'Monat',
  'home.trend': 'Trend (DEMO-Beispiel)',
  'home.demoNoData': 'Beispieldaten — für diese Kennzahl gibt es noch keine Datenquelle',
  'home.passengers': 'Fahrgäste',
  'home.currentPassengers': 'Fahrgäste aktuell',
  'home.estimatedTotal': 'Geschätzte Gesamtzahl',
  'home.afzMeasured': 'AFZ gezählt',
  'home.wifiEstimate': 'WLAN-Schätzung',
  'home.afzCoverage': 'AFZ-Abdeckung {pct}% von {n} Zügen',
  'home.trainsN': '{n} Züge',
  'home.volume': 'Volumen',
  'home.users': 'Benutzer',
  'home.feeds': 'Datenfeeds',
  'home.received': 'Empfangen',
  'home.errors': 'Fehler',
  'home.feedPublished': 'Weitergeleitet',
  'home.feedFiltered': 'Gefiltert',
  'home.feedFilteredNote': 'Gefiltert = redundante Meldungen ohne Betriebsrelevanz, beim Empfang entfernt.',
  'home.feedPlaceholderNote': 'Grau = Beispielwerte; diese Feeds sind noch nicht instrumentiert.',
  'home.feedNoData': 'Keine Daten',
  'home.feedErrors': '{n} Fehler',
  'home.lastHour': 'Letzte Stunde',
  'home.currentDay': 'Aktueller Tag',
  'home.active': 'Aktiv',
  'home.allMessages': 'Alle Meldungen',
  'home.publishedMessages': 'Gesendete Kapazitätsmeldungen',
  'home.coverage': 'Abdeckung',
  'home.vuFailedAll': '{n} fehlgeschlagen — alle: {reason}',
  'home.vuFailedMostly': '{n} fehlgeschlagen — überwiegend {reason} ({pct}%)',
  'home.vuUntriggered': '{n} nie ausgelöst',
  'home.vuDeparted': '/ {n} Abfahrten',
  'home.vuReason.no uics': 'keine Fahrzeugdaten',
  'home.published': 'Gesendet',
  'home.stops': 'Halte',
  'home.alarmTypes': 'Alarmtypen',
  'home.eventDetector': 'Ereigniserkennung',
  'home.impactAnalysis': 'Auswirkungsanalyse',
  'home.peak': 'Spitze',
  'home.targetStop': 'Ziel-Halt',
  'home.event': 'Ereignis',
  'home.impactConfidence': 'Auswirkung / Konfidenz',
  'home.level.high': 'Hoch',
  'home.level.medium': 'Mittel',
  'home.level.low': 'Niedrig',
  'home.medical': 'Medizinisch',
  'home.aggression': 'Aggression',
  'home.fire': 'Feuer',
  'home.allAlarms': 'Alle Alarme',
  'home.pending': 'Offen',
  'home.done': 'Erledigt',
  'home.currentErrors': 'Aktuelle Fehler',
  'home.errorLog': 'Fehlerprotokoll',
  'home.recentMessages': 'Letzte Meldungen',
  'home.generalNotice': 'Allgemeine Meldung',
  'home.summary': 'Übersicht',
  'home.capacity': 'Kapazität',
  'home.last7': 'Letzte 7 Tage',
  'home.occupancy': 'Belegung',
  'home.freeCapacity': 'Freie Kapazität',
  'home.status.done': 'Erledigt',
  'home.status.pending': 'Offen',
  'home.days': 'Mo,Di,Mi,Do,Fr,Sa,So',
};

const en: Dict = {
  // ---- live2 board ----
  'sidebar.title': 'LIVE TRAINS',
  'sidebar.count': '{n} running',
  'sidebar.none': 'No trains running',
  'sidebar.legend': 'Train status',
  'sidebar.recent': 'Just updated',

  'status.ACTIVE': 'Running',
  'status.ARRIVED': 'Arrived',
  'status.SCHEDULED': 'Scheduled',
  'status.NO_DATA': 'No data',
  'status.CANCELLED': 'Cancelled',

  'view.noSelection': 'No trains currently running.',
  'view.loading': 'Loading data…',
  'view.noData': 'No data for this train.',
  'view.localTime': '(local time, Germany)',
  'view.pastStops': 'Previous stops ({n})',
  'view.hide': 'hide',
  'view.show': 'show',
  'view.currentStop': 'CURRENT STOP',
  'view.nextStops': 'Following stops',
  'view.route': 'Route',
  'view.stopCount': '{n} stops',
  'view.counts': 'Counts {measured}/{called}',
  'view.awaiting': '{n} pending',
  'view.missing': '{n} missing',

  'occ.forecast': 'Forecast',
  'occ.awaiting': 'counting',
  'occ.missing': 'no count',
  'occ.none': 'no data',
  'occ.variance': 'n1 Forecast variance',
  'occ.delta': 'Difference vs forecast (one stop earlier)',
  'terminus.arrived': 'Arrived',
  'terminus.expected': 'Expected arrival',

  'time.arr': 'Arr.',
  'time.dep': 'Dep.',

  'reason.NO_UICS': 'no formation data',
  'reason.INVALID_CARRIAGES': 'invalid carriage count',
  'reason.UIC_EXCEPTION': 'formation query failed',
  'reason.BUILD_FAILED': 'message not built',
  'reason.BAD_RESULT': 'unexpected result',
  'reason.PUBLISH_FAILED': 'publish failed',
  'reason.SKIPPED_PUBLISH': 'publish skipped',
  'reason.NO_VU_MESSAGE': 'no VU message',

  // ---- analysis page ----
  'an.analysis': 'Analysis',
  'an.missingMessages': 'Missing messages',
  'an.hideSidebar': 'Hide',
  'an.showTrains': 'Trains',
  'an.selectTrain': 'Select a train',
  'an.selectTrainHint': 'Select a journey from the sidebar to view stop details',
  'an.date': 'Date',
  'an.filter': 'Filter',
  'an.pickDate': 'Pick a date',
  'an.line': 'Line',
  'an.trains': 'Trains',
  'an.noTrains': 'No trains found.',
  'an.staleShow': 'STALE',
  'an.staleHide': 'STALE OFF',

  'filter.live': 'Live',
  'filter.recent': 'Recent',
  'filter.all': 'All',

  'metrics.title': 'Line {line} — Messages',
  'metrics.total': 'Total',
  'metrics.published': 'Sent',
  'metrics.failed': 'Failed',
  'metrics.noStatus': 'No status',
  'metrics.successRate': '{pct}% success rate',

  'acc.title': 'Forecast quality',
  'acc.lineTitle': 'Line {line} — forecast quality',
  'acc.noData': 'No data',
  'acc.all': 'All',
  'acc.modelClass': 'model class',
  'acc.asSent': 'as sent',
  'acc.samples': 'n={n} samples',
  'acc.hnError': 'H→L error',
  'acc.ofHigh': 'of {n} HIGH',
  'acc.mae': 'MAE',
  'acc.persons': 'people',
  'acc.details': 'Details',
  'acc.lineLabel': 'Line {line}',
  'acc.allLines': 'All lines',
  'acc.partialDay': 'partial day',
  'acc.noSample': 'no samples',
  'acc.placeholder': 'SAMPLE DATA',

  // ---- landing page ----
  'home.nav.liveBoard': 'Live board',
  'home.nav.liveMap': 'Live map',
  'home.nav.analytics': 'Analytics',
  'home.total': 'Total',
  'home.activeJourneys': 'Active journeys',
  'home.liveUnits': 'Live (units)',
  'home.unitsN': '{n} units',
  'home.unitsEstN': '{n} units (est.)',
  'home.missingFormation': 'Missing formation',
  'home.noReading': 'No reading',
  'home.countedShare': '{pct}% directly counted',
  'home.journeysEstimated': '{n} journeys estimated',
  'home.unitsComposition': 'Units: AFZ + WiFi + estimated',
  'home.pax': 'Passengers on board',
  'home.trips': 'Passenger trips (Estimated)',
  'home.tripsHint': '{pct}% of units counted by AFZ',
  'home.lastNDays': '{n} days',
  'home.daysWithData': 'only {have} of {want} days have data',
  'home.live': 'Live',
  'home.day': 'Day',
  'home.week': 'Week',
  'home.month': 'Month',
  'home.trend': 'Trend (DEMO example)',
  'home.demoNoData': 'Sample figures — no data source exists for this metric yet',
  'home.passengers': 'Passengers',
  'home.currentPassengers': 'Current passengers',
  'home.estimatedTotal': 'Estimated total',
  'home.afzMeasured': 'AFZ counted',
  'home.wifiEstimate': 'WiFi estimate',
  'home.afzCoverage': 'AFZ coverage {pct}% of {n} trains',
  'home.trainsN': '{n} trains',
  'home.volume': 'Volume',
  'home.users': 'Users',
  'home.feeds': 'Feeds',
  'home.received': 'Received',
  'home.errors': 'Errors',
  'home.feedPublished': 'Published',
  'home.feedFiltered': 'Filtered',
  'home.feedFilteredNote': 'Filtered = redundant messages with no operational content, removed at ingest.',
  'home.feedPlaceholderNote': 'Grey = sample values; these feeds are not yet instrumented.',
  'home.feedNoData': 'No data',
  'home.feedErrors': '{n} errors',
  'home.lastHour': 'Last hour',
  'home.currentDay': 'Current day',
  'home.active': 'Active',
  'home.allMessages': 'All messages',
  'home.publishedMessages': 'Published capacity messages',
  'home.coverage': 'Coverage',
  'home.vuFailedAll': '{n} failed — all {reason}',
  'home.vuFailedMostly': '{n} failed — mostly {reason} ({pct}%)',
  'home.vuUntriggered': '{n} never triggered',
  'home.vuDeparted': '/ {n} departures',
  'home.vuReason.no uics': 'missing formation',
  'home.published': 'Published',
  'home.stops': 'stops',
  'home.alarmTypes': 'Alarm types',
  'home.eventDetector': 'Event detector',
  'home.impactAnalysis': 'Impact analysis',
  'home.peak': 'Peak',
  'home.targetStop': 'Venue stop',
  'home.event': 'Event',
  'home.impactConfidence': 'Impact / confidence',
  'home.level.high': 'High',
  'home.level.medium': 'Medium',
  'home.level.low': 'Low',
  'home.medical': 'Medical',
  'home.aggression': 'Aggression',
  'home.fire': 'Fire',
  'home.allAlarms': 'All alarms',
  'home.pending': 'Pending',
  'home.done': 'Done',
  'home.currentErrors': 'Current errors',
  'home.errorLog': 'Error log',
  'home.recentMessages': 'Recent messages',
  'home.generalNotice': 'General notice',
  'home.summary': 'Summary',
  'home.capacity': 'Capacity',
  'home.last7': 'Last 7 days',
  'home.occupancy': 'Occupancy',
  'home.freeCapacity': 'Free capacity',
  'home.status.done': 'Completed',
  'home.status.pending': 'Pending',
  'home.days': 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
};

const fr: Dict = {
  // ---- live2 board ----
  'sidebar.title': 'TRAINS EN DIRECT',
  'sidebar.count': '{n} en service',
  'sidebar.none': 'Aucun train en service',
  'sidebar.legend': 'Statut des trains',
  'sidebar.recent': 'Vient d’être mis à jour',

  'status.ACTIVE': 'En circulation',
  'status.ARRIVED': 'Arrivé',
  'status.SCHEDULED': 'Prévu',
  'status.NO_DATA': 'Aucune donnée',
  'status.CANCELLED': 'Supprimé',

  'view.noSelection': 'Aucun train en service actuellement.',
  'view.loading': 'Chargement des données…',
  'view.noData': 'Aucune donnée pour ce train.',
  'view.localTime': '(heure locale, Allemagne)',
  'view.pastStops': 'Arrêts précédents ({n})',
  'view.hide': 'masquer',
  'view.show': 'afficher',
  'view.currentStop': 'ARRÊT ACTUEL',
  'view.nextStops': 'Arrêts suivants',
  'view.route': 'Itinéraire',
  'view.stopCount': '{n} arrêts',
  'view.counts': 'Comptages {measured}/{called}',
  'view.awaiting': '{n} en cours',
  'view.missing': '{n} manquants',

  'occ.forecast': 'Prévision',
  'occ.awaiting': 'comptage en cours',
  'occ.missing': 'pas de comptage',
  'occ.none': 'pas de données',
  'occ.variance': 'Écart prévision n1',
  'occ.delta': 'Écart par rapport à la prévision (arrêt précédent)',
  'terminus.arrived': 'Arrivé',
  'terminus.expected': 'Arrivée prévue',

  'time.arr': 'Arr.',
  'time.dep': 'Dép.',

  'reason.NO_UICS': 'données de rame absentes',
  'reason.INVALID_CARRIAGES': 'nombre de voitures invalide',
  'reason.UIC_EXCEPTION': 'échec de la requête rame',
  'reason.BUILD_FAILED': 'message non généré',
  'reason.BAD_RESULT': 'résultat inattendu',
  'reason.PUBLISH_FAILED': 'échec de l’envoi',
  'reason.SKIPPED_PUBLISH': 'envoi ignoré',
  'reason.NO_VU_MESSAGE': 'aucun message VU',

  // ---- analysis page ----
  'an.analysis': 'Analyse',
  'an.missingMessages': 'Messages manquants',
  'an.hideSidebar': 'Masquer',
  'an.showTrains': 'Trains',
  'an.selectTrain': 'Choisir un train',
  'an.selectTrainHint': 'Choisissez un train dans le panneau pour voir les détails des arrêts',
  'an.date': 'Date',
  'an.filter': 'Filtre',
  'an.pickDate': 'Choisir une date',
  'an.line': 'Ligne',
  'an.trains': 'Trains',
  'an.noTrains': 'Aucun train trouvé.',
  'an.staleShow': 'PÉRIMÉS',
  'an.staleHide': 'PÉRIMÉS MASQUÉS',

  'filter.live': 'Direct',
  'filter.recent': 'Récents',
  'filter.all': 'Tous',

  'metrics.title': 'Ligne {line} — Messages',
  'metrics.total': 'Total',
  'metrics.published': 'Envoyés',
  'metrics.failed': 'Échecs',
  'metrics.noStatus': 'Sans statut',
  'metrics.successRate': '{pct}% de réussite',

  'acc.title': 'Qualité des prévisions',
  'acc.lineTitle': 'Ligne {line} — qualité des prévisions',
  'acc.noData': 'Aucune donnée',
  'acc.all': 'Tous',
  'acc.modelClass': 'classe du modèle',
  'acc.asSent': 'tel qu’envoyé',
  'acc.samples': 'n={n} échantillons',
  'acc.hnError': 'Erreur H→B',
  'acc.ofHigh': 'sur {n} HAUT',
  'acc.mae': 'EAM',
  'acc.persons': 'pers.',
  'acc.details': 'Détails',
  'acc.lineLabel': 'Ligne {line}',
  'acc.allLines': 'Toutes les lignes',
  'acc.partialDay': 'journée partielle',
  'acc.noSample': 'aucun échantillon',
  'acc.placeholder': 'DONNÉES D’EXEMPLE',

  // ---- landing page ----
  'home.nav.liveBoard': 'Tableau live',
  'home.nav.liveMap': 'Carte live',
  'home.nav.analytics': 'Analyses',
  'home.total': 'Total',
  'home.activeJourneys': 'Trajets actifs',
  'home.liveUnits': 'Live (rames)',
  'home.unitsN': '{n} rames',
  'home.unitsEstN': '{n} rames (est.)',
  'home.missingFormation': 'Sans données de rame',
  'home.noReading': 'Sans mesure',
  'home.countedShare': '{pct}% comptés directement',
  'home.journeysEstimated': '{n} trajets estimés',
  'home.unitsComposition': 'Rames : AFZ + WiFi + estimées',
  'home.pax': 'Voyageurs à bord',
  'home.trips': 'Trajets voyageurs (estimés)',
  'home.tripsHint': '{pct}% des rames comptées par AFZ',
  'home.lastNDays': '{n} jours',
  'home.daysWithData': 'seulement {have} jours sur {want} avec données',
  'home.live': 'Live',
  'home.day': 'Jour',
  'home.week': 'Semaine',
  'home.month': 'Mois',
  'home.trend': 'Tendance (exemple DEMO)',
  'home.demoNoData': 'Données d\u2019exemple — aucune source n\u2019existe encore',
  'home.passengers': 'Passagers',
  'home.currentPassengers': 'Passagers actuels',
  'home.estimatedTotal': 'Total estimé',
  'home.afzMeasured': 'Compté par AFZ',
  'home.wifiEstimate': 'Estimation Wi-Fi',
  'home.afzCoverage': 'Couverture AFZ {pct}% de {n} trains',
  'home.trainsN': '{n} trains',
  'home.volume': 'Volume',
  'home.users': 'Utilisateurs',
  'home.feeds': 'Flux',
  'home.received': 'Reçus',
  'home.errors': 'Erreurs',
  'home.feedPublished': 'Publiés',
  'home.feedFiltered': 'Filtrés',
  'home.feedFilteredNote': 'Filtrés = messages redondants sans contenu opérationnel, supprimés à la réception.',
  'home.feedPlaceholderNote': 'Gris = valeurs indicatives; ces flux ne sont pas encore instrumentés.',
  'home.feedNoData': 'Aucune donnée',
  'home.feedErrors': '{n} erreurs',
  'home.lastHour': 'Dernière heure',
  'home.currentDay': 'Jour en cours',
  'home.active': 'Actifs',
  'home.allMessages': 'Tous les messages',
  'home.publishedMessages': 'Messages de capacité publiés',
  'home.coverage': 'Couverture',
  'home.vuFailedAll': '{n} échecs — tous : {reason}',
  'home.vuFailedMostly': '{n} échecs — surtout {reason} ({pct}%)',
  'home.vuUntriggered': '{n} jamais déclenchés',
  'home.vuDeparted': '/ {n} départs',
  'home.vuReason.no uics': 'données de rame manquantes',
  'home.published': 'Publiés',
  'home.stops': 'arrêts',
  'home.alarmTypes': 'Types d’alerte',
  'home.eventDetector': 'Détecteur d’événements',
  'home.impactAnalysis': 'Analyse d’impact',
  'home.peak': 'Pic',
  'home.targetStop': 'Arrêt du lieu',
  'home.event': 'Événement',
  'home.impactConfidence': 'Impact / confiance',
  'home.level.high': 'Élevé',
  'home.level.medium': 'Moyen',
  'home.level.low': 'Faible',
  'home.medical': 'Médical',
  'home.aggression': 'Agression',
  'home.fire': 'Incendie',
  'home.allAlarms': 'Toutes les alertes',
  'home.pending': 'En attente',
  'home.done': 'Traitées',
  'home.currentErrors': 'Erreurs actuelles',
  'home.errorLog': 'Journal d’erreurs',
  'home.recentMessages': 'Messages récents',
  'home.generalNotice': 'Avis général',
  'home.summary': 'Résumé',
  'home.capacity': 'Capacité',
  'home.last7': '7 derniers jours',
  'home.occupancy': 'Occupation',
  'home.freeCapacity': 'Capacité libre',
  'home.status.done': 'Traité',
  'home.status.pending': 'En attente',
  'home.days': 'Lun,Mar,Mer,Jeu,Ven,Sam,Dim',
};

const DICTS: Record<Lang, Dict> = { de, en, fr };

/**
 * Translate. Falls back to German, then to the key itself — a missing
 * translation shows the key rather than blank, so it is obvious in review
 * rather than invisible.
 */
export function translate(lang: Lang, key: string, vars?: Record<string, string | number>): string {
  const raw = DICTS[lang]?.[key] ?? DICTS.de[key] ?? key;
  if (!vars) return raw;
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v)),
    raw
  );
}
